import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Select,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type {
  ComplianceReport,
  PortfolioReport,
  PortfolioRow,
  UtilisationReport,
} from "../../shared/types/api";
import { HEALTH_TONE, STATUS_LABELS } from "../projects/projectVocabulary";
import { formatHoursAsTime, monthBounds, shiftMonths } from "../timesheets/timesheetVocabulary";
import { EmployeeScorecard } from "./performance/EmployeeScorecard";
import { Leaderboard } from "./performance/Leaderboard";
import { MissedDeadlines } from "./performance/MissedDeadlines";
import { EstimateAccuracy } from "./performance/EstimateAccuracy";
import { Throughput } from "./performance/Throughput";

type Tab =
  | "portfolio"
  | "compliance"
  | "utilisation"
  | "leaderboard"
  | "scorecard"
  | "missed"
  | "estimates"
  | "throughput";

const TABS: { key: Tab; label: string; permission: string }[] = [
  { key: "portfolio", label: "Portfolio", permission: "REPORT_ORGANIZATION_VIEW" },
  { key: "leaderboard", label: "Leaderboard", permission: "REPORT_ORGANIZATION_VIEW" },
  { key: "throughput", label: "Throughput", permission: "REPORT_ORGANIZATION_VIEW" },
  { key: "scorecard", label: "Employee scorecard", permission: "REPORT_ORGANIZATION_VIEW" },
  { key: "missed", label: "Missed deadlines", permission: "REPORT_ORGANIZATION_VIEW" },
  { key: "estimates", label: "Estimate accuracy", permission: "REPORT_ORGANIZATION_VIEW" },
  { key: "compliance", label: "Timesheet compliance", permission: "REPORT_ORGANIZATION_VIEW" },
  { key: "utilisation", label: "Utilisation", permission: "REPORT_RESOURCE_VIEW" },
];

/**
 * §99 and §100's reports.
 *
 * <p>Each is read from the same service the corresponding screen reads, so a figure here and the
 * page it links to cannot disagree. That matters more than it sounds: a portfolio assembled from
 * its own tables is one that drifts, and the reader has no way of telling which number is right.
 *
 * <p>Every row links through to the thing it describes. A report that can only be looked at is a
 * report people stop looking at.
 */
export function ReportsPage() {
  const { can } = useAuth();
  const available = TABS.filter((tab) => can(tab.permission));
  const [tab, setTab] = useState<Tab>(available[0]?.key ?? "portfolio");

  return (
    <div className="space-y-5">
      <PageHeader
        title="Reports"
        subtitle="Assembled from the same records the screens show, so the two cannot disagree."
      />

      {available.length > 1 && (
        <div className="flex gap-1 border-b border-ink-200">
          {available.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => setTab(entry.key)}
              className={cn(
                "-mb-px border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
                tab === entry.key
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-ink-600 hover:text-ink-900",
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>
      )}

      {tab === "portfolio" && <Portfolio />}
      {tab === "leaderboard" && <Leaderboard />}
      {tab === "throughput" && <Throughput />}
      {tab === "scorecard" && <EmployeeScorecard />}
      {tab === "missed" && <MissedDeadlines />}
      {tab === "estimates" && <EstimateAccuracy />}
      {tab === "compliance" && <Compliance />}
      {tab === "utilisation" && <Utilisation />}
    </div>
  );
}

/** §99: every project, worst first. */
function Portfolio() {
  const [includeClosed, setIncludeClosed] = useState("false");

  const report = useQuery({
    queryKey: ["report", "portfolio", includeClosed],
    queryFn: () =>
      api.get<PortfolioReport>(`/api/v1/reports/portfolio?includeClosed=${includeClosed}`),
  });

  if (report.isPending) {
    return <LoadingState label="Building the portfolio" />;
  }
  if (report.isError) {
    return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;
  }

  const data = report.data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure label="Projects" value={String(data.projectCount)} note="live right now" />
        <Figure
          label="Committed"
          value={formatHoursAsTime(data.totalBaselineHours)}
          note="across approved baselines"
        />
        <Figure
          label="Spent so far"
          value={formatHoursAsTime(data.totalActualHours)}
          note={`forecast ${formatHoursAsTime(data.totalEstimateAtCompletionHours)}`}
        />
        <Figure
          label="Needing attention"
          value={String(data.redCount + data.amberCount)}
          note={`${data.redCount} red · ${data.slippingCount} slipping`}
          tone={data.redCount > 0 ? "red" : data.amberCount > 0 ? "amber" : undefined}
        />
      </div>

      {data.withoutBaselineCount > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <strong className="font-semibold">
            {data.withoutBaselineCount}{" "}
            {data.withoutBaselineCount === 1 ? "project has" : "projects have"} no approved
            baseline.
          </strong>{" "}
          Their variance columns are empty because there is nothing to vary from — not because
          they are on plan.
        </div>
      )}

      <Card>
        <CardHeader
          title="Every project"
          subtitle="Worst health first, then by how far the finish date has moved."
          action={
            <Select
              value={includeClosed}
              onChange={(event) => setIncludeClosed(event.target.value)}
            >
              <option value="false">Live projects</option>
              <option value="true">Include closed</option>
            </Select>
          }
        />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                  <th className="px-4 py-2.5 font-medium">Project</th>
                  <th className="px-4 py-2.5 font-medium">Manager</th>
                  <th className="px-4 py-2.5 font-medium">Health</th>
                  <th className="px-4 py-2.5 text-right font-medium">Committed</th>
                  <th className="px-4 py-2.5 text-right font-medium">Spent</th>
                  <th className="px-4 py-2.5 text-right font-medium">Forecast</th>
                  <th className="px-4 py-2.5 text-right font-medium">Variance</th>
                  <th className="px-4 py-2.5 text-right font-medium">Finish</th>
                  <th className="px-4 py-2.5 text-right font-medium">Blockers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.projects.map((row) => (
                  <PortfolioTableRow key={row.projectId} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function PortfolioTableRow({ row }: { row: PortfolioRow }) {
  return (
    <tr className="hover:bg-ink-50">
      <td className="px-4 py-3">
        <Link to={`/projects/${row.projectId}`} className="hover:text-brand-600">
          <span className="mr-2 font-mono text-xs text-ink-500">{row.projectKey}</span>
          <span className="text-ink-900">{row.projectName}</span>
        </Link>
        <span className="mt-0.5 block text-xs text-ink-500">
          {STATUS_LABELS[row.status]} · {row.openItemCount} open
        </span>
      </td>
      <td className="px-4 py-3 text-ink-700">{row.projectManagerName ?? "—"}</td>
      <td className="px-4 py-3">
        <StatusBadge tone={HEALTH_TONE[row.health]}>{row.health}</StatusBadge>
        {row.healthOverridden && (
          <span className="mt-0.5 block text-xs text-ink-500">set manually</span>
        )}
      </td>
      <td className="tnum px-4 py-3 text-right text-ink-700">
        {row.hasBaseline ? formatHoursAsTime(row.baselineHours) : <NoBaseline />}
      </td>
      <td className="tnum px-4 py-3 text-right text-ink-900">
        {formatHoursAsTime(row.actualHours)}
      </td>
      <td className="tnum px-4 py-3 text-right text-ink-700">
        {formatHoursAsTime(row.estimateAtCompletionHours)}
      </td>
      <td className="tnum px-4 py-3 text-right">
        {row.varianceSharePercent == null ? (
          <NoBaseline />
        ) : (
          <span
            className={
              row.varianceSharePercent > 5
                ? "text-amber-700"
                : row.varianceSharePercent < -5
                  ? "text-emerald-700"
                  : "text-ink-700"
            }
          >
            {row.varianceSharePercent > 0 ? "+" : ""}
            {Math.round(row.varianceSharePercent)}%
          </span>
        )}
      </td>
      <td className="tnum px-4 py-3 text-right">
        <span className="text-ink-700">{row.forecastFinish ?? "—"}</span>
        {row.scheduleSlippageDays != null && row.scheduleSlippageDays !== 0 && (
          <span
            className={cn(
              "mt-0.5 block text-xs",
              row.scheduleSlippageDays > 0 ? "text-amber-700" : "text-emerald-700",
            )}
          >
            {row.scheduleSlippageDays > 0 ? "+" : ""}
            {row.scheduleSlippageDays} d
          </span>
        )}
      </td>
      <td className="tnum px-4 py-3 text-right">
        {row.criticalBlockerCount > 0 ? (
          <span className="text-red-700">{row.criticalBlockerCount}</span>
        ) : (
          <span className="text-ink-400">—</span>
        )}
      </td>
    </tr>
  );
}

/** Says why a cell is empty, rather than leaving the reader to guess it means zero. */
function NoBaseline() {
  return (
    <span className="text-xs text-ink-500" title="No approved baseline to measure against">
      no baseline
    </span>
  );
}

/** §100: who has accounted for their time. */
function Compliance() {
  const [offset, setOffset] = useState("1");
  const period = monthBounds(shiftMonths(monthBounds(new Date()).periodStart, -Number(offset)));

  const report = useQuery({
    queryKey: ["report", "compliance", period.periodStart],
    queryFn: () =>
      api.get<ComplianceReport>(
        `/api/v1/reports/timesheet-compliance?periodStart=${period.periodStart}&periodEnd=${period.periodEnd}`,
      ),
  });

  if (report.isPending) {
    return <LoadingState label="Checking the period" />;
  }
  if (report.isError) {
    return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;
  }

  const data = report.data;
  const outstanding = data.people.filter((row) => !row.submitted);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure label="People" value={String(data.peopleCount)} note="employed in the period" />
        <Figure
          label="Submitted"
          value={`${data.submittedCount}/${data.peopleCount}`}
          note={outstanding.length === 0 ? "everybody" : `${outstanding.length} outstanding`}
          tone={outstanding.length > 0 ? "amber" : undefined}
        />
        <Figure
          label="Hours expected"
          value={formatHoursAsTime(data.totalExpectedHours)}
          note="net of holidays and leave"
        />
        <Figure
          label="Hours logged"
          value={formatHoursAsTime(data.totalLoggedHours)}
          note={`${data.completeCount} people complete`}
        />
      </div>

      <Card>
        <CardHeader
          title="By person"
          subtitle="Largest gap first."
          action={
            <Select value={offset} onChange={(event) => setOffset(event.target.value)}>
              <option value="0">This month</option>
              <option value="1">Last month</option>
              <option value="2">Two months ago</option>
              <option value="3">Three months ago</option>
            </Select>
          }
        />
        <CardBody className="p-0">
          {data.people.length === 0 ? (
            <EmptyState title="Nobody to report on" message="No active employees in this period." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                    <th className="px-4 py-2.5 font-medium">Person</th>
                    <th className="px-4 py-2.5 text-right font-medium">Expected</th>
                    <th className="px-4 py-2.5 text-right font-medium">Logged</th>
                    <th className="px-4 py-2.5 text-right font-medium">Missing</th>
                    <th className="px-4 py-2.5 font-medium">Period</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {data.people.map((row) => (
                    <tr key={row.employeeId} className="hover:bg-ink-50">
                      <td className="px-4 py-3">
                        <span className="text-ink-900">{row.employeeName}</span>
                        {row.department && (
                          <span className="mt-0.5 block text-xs text-ink-500">
                            {row.department}
                          </span>
                        )}
                      </td>
                      <td className="tnum px-4 py-3 text-right text-ink-700">
                        {formatHoursAsTime(row.expectedHours)}
                      </td>
                      <td className="tnum px-4 py-3 text-right text-ink-900">
                        {formatHoursAsTime(row.loggedHours)}
                      </td>
                      <td className="tnum px-4 py-3 text-right">
                        {(row.missingHours ?? 0) > 0 ? (
                          <span className="text-amber-700">
                            {formatHoursAsTime(row.missingHours)}
                          </span>
                        ) : (
                          <span className="text-emerald-700">none</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={COMPLIANCE_TONE[row.status] ?? "gray"}>
                          {COMPLIANCE_LABELS[row.status] ?? row.status}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * A period's state in words.
 *
 * <p>Deliberately not a yes/no. "Not compliant" covers both somebody who has logged nothing and
 * somebody whose finished month is sitting in draft waiting on one click, and those are different
 * conversations.
 */
const COMPLIANCE_LABELS: Record<string, string> = {
  NOT_STARTED: "Not started",
  DRAFT: "In draft",
  SUBMITTED: "Waiting for approval",
  APPROVED: "Approved",
  REJECTED: "Sent back",
  LOCKED: "Locked",
};

const COMPLIANCE_TONE: Record<string, "gray" | "amber" | "green" | "red" | "blue"> = {
  NOT_STARTED: "red",
  DRAFT: "amber",
  SUBMITTED: "amber",
  APPROVED: "green",
  REJECTED: "red",
  LOCKED: "blue",
};

/** §100: who is over-promised. */
function Utilisation() {
  const [weeks, setWeeks] = useState("4");
  const from = new Date();
  const to = new Date(from.getTime() + Number(weeks) * 7 * 86_400_000);

  const report = useQuery({
    queryKey: ["report", "utilisation", weeks],
    queryFn: () =>
      api.get<UtilisationReport>(
        `/api/v1/reports/utilisation?from=${iso(from)}&to=${iso(to)}`,
      ),
  });

  if (report.isPending) {
    return <LoadingState label="Working out capacity" />;
  }
  if (report.isError) {
    return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;
  }

  const data = report.data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure label="People" value={String(data.peopleCount)} note="active" />
        <Figure
          label="Over-promised"
          value={String(data.overallocatedCount)}
          note="promised more than exists"
          tone={data.overallocatedCount > 0 ? "red" : undefined}
        />
        <Figure
          label="Under-committed"
          value={String(data.underallocatedCount)}
          note="below 60% of available"
          tone={data.underallocatedCount > 0 ? "amber" : undefined}
        />
        <Figure
          label="Average"
          value={
            data.averageUtilisationPercent == null
              ? "—"
              : `${Math.round(data.averageUtilisationPercent)}%`
          }
          note="of allocatable time"
        />
      </div>

      <Card>
        <CardHeader
          title="By person"
          subtitle="Most over-promised first. Holidays and approved leave are already taken out."
          action={
            <Select value={weeks} onChange={(event) => setWeeks(event.target.value)}>
              <option value="2">Next 2 weeks</option>
              <option value="4">Next 4 weeks</option>
              <option value="12">Next quarter</option>
            </Select>
          }
        />
        <CardBody className="p-0">
          {data.people.length === 0 ? (
            <EmptyState title="Nobody to report on" message="No active employees." />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.people.map((row) => (
                <li key={row.employeeId} className="px-4 py-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm text-ink-900">
                      {row.employeeName}
                      {row.department && (
                        <span className="ml-2 text-xs text-ink-500">{row.department}</span>
                      )}
                    </span>
                    <span className="flex items-center gap-3 text-sm">
                      <span className="tnum text-ink-600">
                        {formatHoursAsTime(row.allocatedHours)} of{" "}
                        {formatHoursAsTime(row.availableHours)}
                      </span>
                      <span
                        className={cn(
                          "tnum w-12 text-right font-medium",
                          row.overallocated
                            ? "text-red-700"
                            : row.underallocated
                              ? "text-amber-700"
                              : "text-emerald-700",
                        )}
                      >
                        {Math.round(row.utilisationPercent ?? 0)}%
                      </span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink-100">
                    <div
                      className={cn(
                        "h-full rounded-full",
                        row.overallocated
                          ? "bg-red-500"
                          : row.underallocated
                            ? "bg-amber-500"
                            : "bg-emerald-500",
                      )}
                      style={{ width: `${Math.min(100, row.utilisationPercent ?? 0)}%` }}
                    />
                  </div>
                  {row.projects.length > 0 && (
                    <p className="mt-1 text-xs text-ink-500">{row.projects.join(" · ")}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Figure({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone?: "amber" | "red";
}) {
  return (
    <div className="rounded-xl border border-ink-200 bg-surface px-4 py-3">
      <p className="text-xs text-ink-500">{label}</p>
      <p
        className={cn(
          "tnum mt-1 font-display text-2xl font-semibold",
          tone === "red" ? "text-red-700" : tone === "amber" ? "text-amber-700" : "text-ink-900",
        )}
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-ink-500">{note}</p>
    </div>
  );
}

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}
